import { img, IMG_W, IMG_H, srcSetFor } from "@/assets/images";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, ShieldCheck, Gem, Clock, HeartHandshake, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";

const reasons = [
  {
    icon: Gem,
    title: "Premium Workmanship",
    desc: "Careful prep, disciplined finishing, and a clean handoff on every project — built to last and look right the first time.",
  },
  {
    icon: ShieldCheck,
    title: "Fully Vetted Team",
    desc: "Trained crews, clear communication, and respectful service from first call to final walkthrough.",
  },
  {
    icon: Clock,
    title: "On-Time, Every Time",
    desc: "Fast scheduling, accurate timelines, and crews that show up when promised — no guessing, no chasing.",
  },
  {
    icon: HeartHandshake,
    title: "Honest, Upfront Pricing",
    desc: "Free estimates with transparent scope. No surprise add-ons, no high-pressure sales tactics — ever.",
  },
  {
    icon: Sparkles,
    title: "Detail-Obsessed Finish",
    desc: "We sweat the small things — clean lines, tidy job sites, and a final walkthrough you can sign off on with confidence.",
  },
  {
    icon: CheckCircle,
    title: "Responsive Support",
    desc: "Real people, quick answers, and follow-through after the job is done. You're never left wondering what's next.",
  },
];

const stats = [
  { value: "15+", label: "Years Experience" },
  { value: "500+", label: "Projects Completed" },
  { value: "100%", label: "Satisfaction Focus" },
  { value: "24h", label: "Response Time" },
];

const WhyChooseUs = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="why-choose-us" className="relative section-padding bg-background overflow-hidden">
      {/* Soft brand accents */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full blur-3xl opacity-[0.18] pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--primary)), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-32 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-[0.14] pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary)), transparent 70%)" }}
      />

      <div className="container-max max-w-[1200px] relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Sticky editorial intro */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="eyebrow-pill mb-6"
              >
                <Sparkles className="h-3 w-3" /> Why Choose Us
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-heading font-bold text-foreground tracking-tight leading-[1.02]"
                style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}
              >
                Built on{" "}
                <span className="relative inline-block">
                  <span className="text-gradient-primary">quality.</span>
                  <span
                    aria-hidden
                    className="absolute left-0 right-0 -bottom-2 h-[6px] rounded-full opacity-70"
                    style={{ background: "linear-gradient(90deg, hsl(var(--primary)/0.6), transparent)" }}
                  />
                </span>
                <br />
                Delivered with care.
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mt-6 text-foreground/80 text-lg leading-relaxed max-w-md"
              >
                Six commitments that shape every project we take on — from the first phone call to the final walkthrough.
              </motion.p>

              {/* Stat strip */}
              <div className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5 max-w-md">
                {stats.map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                    className="border-l-2 border-primary/40 pl-4"
                  >
                    <div className="font-heading font-bold text-3xl text-foreground tracking-tight leading-none">
                      {s.value}
                    </div>
                    <div className="mt-1.5 text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">
                      {s.label}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-10">
                <Button size="lg" onClick={openQuoteForm} className="gap-2 btn-glow rounded-full px-6">
                  Get Free Estimate <ArrowUpRight className="h-4 w-4" />
                </Button>
              </div>

              {/* Showcase image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mt-10 rounded-2xl overflow-hidden border border-border/60 shadow-premium"
              >
                <img
                  src={img.whyChooseShowcase}
                  srcSet={srcSetFor(img.whyChooseShowcase)}
                  sizes="(min-width: 1024px) 440px, 92vw"
                  alt="Good Choice Cleaning crew delivering professional residential cleaning results"
                  className="w-full h-auto object-cover"
                  width={IMG_W}
                  height={IMG_H}
                  loading="lazy"
                  decoding="async"
                />
              </motion.div>
            </div>
          </div>

          {/* Numbered reasons list */}
          <div className="lg:col-span-7">
            <ul className="divide-y divide-border/70 border-y border-border/70">
              {reasons.map((r, i) => {
                const Icon = r.icon;
                return (
                  <motion.li
                    key={r.title}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: i * 0.06, duration: 0.5, ease: [0.33, 1, 0.68, 1] }}
                    className="group relative"
                  >
                    {/* Left accent rail that fills on hover */}
                    <span
                      aria-hidden
                      className="absolute left-0 top-0 bottom-0 w-[3px] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-[cubic-bezier(0.33,1,0.68,1)]"
                      style={{ background: "linear-gradient(180deg, hsl(var(--primary)), hsl(var(--secondary)))" }}
                    />
                    <div className="flex items-start gap-5 sm:gap-7 py-7 sm:py-8 pl-5 sm:pl-7 pr-2 transition-colors duration-500 group-hover:bg-muted/40">
                      <div className="shrink-0 flex flex-col items-start gap-3">
                        <span
                          className="font-heading font-bold text-[2.25rem] sm:text-[2.75rem] leading-none text-muted-foreground/40 group-hover:text-primary transition-colors duration-500"
                          aria-hidden
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20 group-hover:bg-primary group-hover:text-primary-foreground group-hover:ring-primary transition-all duration-500">
                          <Icon className="h-5 w-5" strokeWidth={1.85} />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <h3 className="font-heading font-bold text-xl sm:text-2xl text-foreground tracking-tight leading-snug">
                            {r.title}
                          </h3>
                          <ArrowUpRight
                            aria-hidden
                            className="h-5 w-5 text-muted-foreground/40 shrink-0 mt-1 transition-all duration-500 group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                          />
                        </div>
                        <p className="mt-2.5 text-foreground/80 text-[15px] leading-relaxed max-w-[60ch]">
                          {r.desc}
                        </p>
                      </div>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
